<template>
	<div>
		<BasePlaceHolder
			v-if="isAuthenticated"
			:placehold="loading"
			:line-count="2"
			:theme="isDarkMode ? 'dark' : 'light'"
			:class="{ 'display-flex justify-content-center': centerButton && !loading }"
		>
			<BaseLink
				:class="['button button-primary', { 'width-100': !appStore.isDesktop }]"
				:link="link"
				:data-test="`${componentId}-auth-link`"
			/>
		</BasePlaceHolder>
		<LeadCaptureComponent
			v-else-if="props.formConfig"
			:form-config="props.formConfig"
			:component-id="props.componentId"
		/>
	</div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { FundDirectInvestDetails } from 'types/funds';
import { getFund } from '@sanity/proxy/funds';
import { getInvestFlowLink } from '@utils/invest';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import { Link } from 'types/layout';
import { useAppStore } from '@stores/app';
import { useRoute } from 'vue-router';
import { useUserStore } from '@stores/user';

interface Props {
	formConfig?: LeadCaptureFormConfig;
	centerButton?: boolean;
	componentId?: string;
	isDarkMode?: boolean;
	offeringId?: string;
}

const props = withDefaults(defineProps<Props>(), {
	formConfig: undefined,
	centerButton: false,
	componentId: undefined,
	isDarkMode: false,
	offeringId: undefined
});

const route = useRoute();
const appStore = useAppStore();
const userStore = useUserStore();

const isAuthenticated = appStore.isAuthenticated;

const loading = ref(true);
const directInvestDetails = ref<FundDirectInvestDetails | null>(null);

const link = computed((): Link => {
	const investLink = { text: 'Invest' };
	if (userStore.isExistingInvestor) {
		if (directInvestDetails.value?.entityEligibleForEDirectInvestmentIntoReit) {
			return {
				...getInvestFlowLink('fund', investLink.text, {
					backRoute: route.name as string,
					offeringId: directInvestDetails.value.reitOfferingVintageId
				}),
				state: { backRoute: route.name }
			};
		} else {
			return getInvestFlowLink('plan', 'Invest', { backRoute: route.name as string });
		}
	} else {
		return {
			text: 'Invest',
			router: 'checkout-resume'
		};
	}
});

async function setup(): Promise<void> {
	if (isAuthenticated && userStore.isExistingInvestor && !!props.offeringId) {
		const fund = await getFund(props.offeringId);
		directInvestDetails.value = fund?.directInvestmentDetails ?? null;
	}

	loading.value = false;
}

setup();
</script>
